import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';
import { abrirAlertaTemprana } from 'src/app/common/alerta-temprana.utils';
import {
  esTipoCitaCampo,
  esTipoCitaInforme
} from 'src/app/common/tipo-cita.utils';
import { CumplimientoRepository } from 'src/app/domain/service/cumplimiento/cumplimiento-repository';
import { HttpOficinaRepository } from 'src/app/infrastructure/adapters/catalogo/http-oficina.repository';
import { HttpAseguradoraRepository } from 'src/app/infrastructure/adapters/aseguradora/http-aseguradora.repository';
import { Oficina } from 'src/app/domain/model/catalogo/oficina';
import { OrdenServicioRepository } from 'src/app/domain/service/orden-servicio/orden-servicio-repository';
import {
  CumplimientoFiltro,
  ProgramacionPendiente,
  CumplimientoRequest,
  SaldoResponse,
  HistorialCumplimiento,
  DatosActaRequest,
  RiesgoPotencialFiltro
} from 'src/app/domain/model/cumplimiento/cumplimiento';
import {
  DEFAULT_ROWS_PER_PAGE,
  PAGE_SIZE_OPTIONS,
  ESTADO_CITA,
  TIPO_CITA,
  TIPO_EMPLEADO
} from 'src/app/common/constants-ejb';
import { calcularDiasHabiles, dateToString } from 'src/app/common/date-utils';
import { HttpUsuarioRepository } from 'src/app/infrastructure/adapters/usuario/http-usuario.repository';
import { PersonaRepository } from 'src/app/domain/service/persona/persona-repository';
import { PoblacionRepository } from 'src/app/domain/service/transporte/poblacion-repository';
import { Persona, PersonaFiltro } from 'src/app/domain/model/persona/persona';
import { AsesorSeleccionado } from '../../common/busquedas/busqueda-asesor-dialog.component';
import {
  GestionContactosInformePendiente,
  gestionContactosInformeSiAplica
} from 'src/app/presentation/shared/contactos/contactos-informe.utils';

/**
 * Interface para municipio (transporte)
 */
export interface Municipio {
  cdsura: string;
  cdmunicipio: string;
  nombre: string;
  departamento?: string;
}

/**
 * Interface para campos seleccionados con lupa
 * Para Consultor incluye campos adicionales de persona (nombre1/2, apellido1/2)
 */
export interface CampoSeleccionadoUI {
  id: number | null;
  codigo?: string;
  descripcion: string;
  /** Para Consultor/Aseguradora: nombre que se muestra en el campo "Nombre" */
  nombreComercial?: string;
  /** Tipo de identificación (CC, NIT, CE, etc.) */
  tipoIdentificacion?: string;
  /** Para Consultor: campos de persona (Tpsc_personasdto) */
  nombre1?: string;
  nombre2?: string;
  apellido1?: string;
  apellido2?: string;
  /** Indica si fue seleccionado desde dialog (id=cdpersona) vs precarga (id=nmconsecutivo) */
  esDesdeDialog?: boolean;
}

/**
 * Interface de filtros de UI (con objetos para lupas)
 * Compatible con FiltrosCumplimiento del componente
 */
export interface FiltrosUI {
  ordenServicio?: string;
  cdoficina?: string;
  unidadAdministrativa?: CampoSeleccionadoUI;
  tarea?: CampoSeleccionadoUI;
  consultor?: CampoSeleccionadoUI;
  aseguradora?: CampoSeleccionadoUI;
  cliente?: CampoSeleccionadoUI;
  centroTrabajo?: CampoSeleccionadoUI;
}

/**
 * Facade para Cumplimientos Programados
 *
 * Contiene toda la lógica de negocio y estado reactivo.
 * El componente solo delega a este servicio.
 *
 * Flujo Legacy (FilterOrdenesProgViewImpl.java):
 * 1. Filtros de búsqueda (orden, oficina, cliente, asesor, fechas, tarea)
 * 2. Tabla de programaciones pendientes (PRO, EJP)
 * 3. Seleccionar programación → Abrir formulario de cumplimiento
 * 4. Registrar cumplimiento con validaciones
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable()
export class CumplimientosProgramadosFacade {
  // === Servicios ===
  private readonly router = inject(Router);
  private readonly http = inject(HttpClient);
  private readonly messageService = inject(MessageService);
  private readonly cumplimientoRepo = inject(CumplimientoRepository);
  private readonly oficinaRepo = inject(HttpOficinaRepository);
  private readonly aseguradoraRepo = inject(HttpAseguradoraRepository);
  private readonly usuarioRepo = inject(HttpUsuarioRepository);
  private readonly personaRepo = inject(PersonaRepository);
  private readonly ordenServicioRepo = inject(OrdenServicioRepository);
  private readonly poblacionRepo = inject(PoblacionRepository);

  // === Estado Principal ===
  readonly loading = signal(false);
  /**
   * Gestión de contactos del informe pendiente de abrir tras guardar un cumplimiento con informe
   * Final (legacy CreateOrUpdateCumplimientoActivity.gestionarContactosInforme). La consume el
   * contenedor, que es quien muestra el diálogo.
   */
  readonly contactosInformePendiente = signal<GestionContactosInformePendiente | null>(null);
  readonly busquedaRealizada = signal(false);
  readonly allData = signal<ProgramacionPendiente[]>([]);
  readonly selectedProgramacion = signal<ProgramacionPendiente | null>(null);
  readonly riesgoConteoLoading = signal(false);
  readonly riesgoConteoTotal = signal(0);
  readonly riesgoConteoAbiertos = signal(0);
  readonly riesgoConteoCerrados = signal(0);
  // Filtro API actual para reutilizar en paginación del servidor
  private filtroApiActual: CumplimientoFiltro = {};

  // === Estado del Formulario de Cumplimiento ===
  readonly dialogFormVisible = signal(false);
  readonly formLoading = signal(false);
  readonly saldoActual = signal(0);
  readonly requiereJustificacion = signal(false);
  readonly calculandoTarifa = signal(false);
  readonly actaError = signal<string | null>(null);
  readonly actaGenerando = signal(false);
  readonly mostrarBotonReintentar = signal(false);
  readonly mostrarBotonDescargarActa = signal(false);
  readonly descargandoActa = signal(false);
  private nmconsecutivoCumplimientoGuardado: number | null = null;
  private nmconsecutivoCumplimientoDescargable: number | null = null;
  private ultimaDatosActa: CumplimientoRequest['datosActa'] | null = null;

  // === Configuración Cumplimiento Tardío ===
  // Legacy: CreateOrUpdateCumplimientoActivity.loadParameters() + loadParametersHoliDays()
  // LIMIT_DAYS_PROGRAM: Máximo de días hábiles entre fecha programación y cumplimiento
  // Festivos (T094_DIAS_FESTIVOS): lista para excluir del cálculo de días hábiles
  readonly limitDaysProgram = signal<number>(0);
  readonly festivos = signal<string[]>([]);
  readonly loadingConfigTardio = signal(false);
  /** true si la última carga de límite de días y festivos falló (la tardanza no se puede validar). */
  readonly configTardioError = signal(false);

  // === Estado Transporte ===
  readonly municipiosSugeridos = signal<Municipio[]>([]);
  readonly municipioOrigen = signal<Municipio | null>(null);
  readonly municipioDestino = signal<Municipio | null>(null);

  // === Filtros - Fechas ===
  readonly fechaInicial = signal<Date | null>(null);
  readonly fechaFinal = signal<Date | null>(null);

  // === Estado Diálogos de Búsqueda (lupas) ===
  readonly mostrarDialogUnidadAdmin = signal(false);
  readonly mostrarDialogTarea = signal(false);
  readonly mostrarDialogConsultor = signal(false);
  readonly mostrarDialogAseguradora = signal(false);
  readonly mostrarDialogCliente = signal(false);
  readonly mostrarDialogCentroTrabajo = signal(false);

  // === Estado Usuario/Sesión ===
  // Legacy: GeneralParameters.CDTIPO_EMP_ASES - Si es asesor, se precargan sus datos en Consultor
  readonly esAsesor = signal(false);
  readonly consultorPrecargado = signal<CampoSeleccionadoUI | null>(null);

  // Legacy: ApplicationCommonData.getDefaultClient() - Aseguradora por defecto
  // Se carga por contrato dedicado en backend (/aseguradoras/default)
  readonly aseguradoraPrecargada = signal<CampoSeleccionadoUI | null>(null);

  // === Filtros - Inicializados vacíos (usuario NO es asesor) ===
  readonly filtro = signal<FiltrosUI>({});

  // === Catálogos ===
  readonly oficinas = signal<Oficina[]>([]);
  readonly justificaciones = signal<{ label: string; value: number }[]>([
    { label: 'Reprogramación por cliente', value: 1 },
    { label: 'Reprogramación por consultor', value: 2 },
    { label: 'Incapacidad del consultor', value: 3 },
    { label: 'Fuerza mayor', value: 4 },
    { label: 'Otro', value: 99 }
  ]);

  // === Paginación keyset/cursor (skill acceso-datos-oracle §7 — nunca OFFSET) ===
  // currentPage es solo para mostrar "Página X de Y" — con keyset no hay
  // número de página real, se lleva localmente por navegación.
  readonly currentPage = signal(1);
  readonly pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  // totalElements del servidor - para paginación del lado del servidor.
  // El backend SÍ sigue calculando totalElements para este endpoint (cuenta
  // en paralelo al contenido en cada request, ver
  // R2dbcCumplimientoRepositoryAdapter#listarProgramacionesPendientes), por
  // lo que currentPage/totalPages/hasNext/hasPrevious/registroInicio/
  // registroFin siguen siendo exactos pese a que la navegación real usa
  // Keyset/Cursor (contrato-paginacion-keyset-lote4.md, apéndice "Lote 4
  // completado"). Lo único que cambia es CÓMO se piden las páginas: con
  // cursor/direction en vez de page/size — no hay salto a página arbitraria,
  // pero "primera"/"última"/"anterior"/"siguiente" siguen funcionando
  // porque currentPage avanza siempre de forma secuencial y el backend
  // soporta direction=PREVIOUS sin cursor para saltar a la última página.
  readonly totalElements = signal(0);
  private nextCursorActual: string | null = null;
  private previousCursorActual: string | null = null;

  // === Historial de Cumplimientos (Legacy: ListarCumplimientosViewImpl.java) ===
  readonly mostrarHistorialDialog = signal(false);
  readonly historialCumplimientos = signal<HistorialCumplimiento[]>([]);
  readonly loadingHistorial = signal(false);

  // === Modo "Asociar Acta" (soloActa) desde historial ===
  /** Cuando true, el form de cumplimiento se abre en modo "solo acta". */
  readonly soloActaModo = signal(false);
  /** Consecutivo del cumplimiento que se está re-asociando en modo soloActa. */
  private nmconsecutivoCumplimientoSoloActa: number | null = null;
  /** Programación original antes de entrar en soloActa (para restaurar al volver). */
  private _programacionOriginalPreSoloActa: ProgramacionPendiente | null = null;
  /** Indica que se asoció acta recientemente (muestra banner de recarga en historial). */
  readonly actaRecienAsociada = signal(false);
  readonly origenInformacionTareas = signal(false);

  // === Observaciones/Recomendaciones Dialog ===
  readonly mostrarObservacionesDialog = signal(false);
  readonly cumplimientoSeleccionadoObs = signal<HistorialCumplimiento | null>(
    null
  );

  // === Configuración externa (parámetros generales) ===
  readonly contratosCumpl = signal<string[]>([]);
  /** Versión CSV de contratosCumpl para pasar al formulario-cumplimiento */
  readonly contratosCumplString = computed(() =>
    this.contratosCumpl().join(',')
  );

  // === Computed: Paginación (servidor) ===
  readonly totalRegistros = computed(() => this.totalElements());
  readonly totalPages = computed(
    () => Math.ceil(this.totalRegistros() / this.pageSize()) || 1
  );

  // Flags de navegación: vienen de la respuesta del servidor (skill
  // frontend-girop §17.7), nunca se deducen de currentPage/totalPages.
  readonly hasPrevious = signal(false);
  readonly hasNext = signal(false);

  // allData ya contiene solo los datos de la página actual (del servidor)
  readonly paginatedData = computed(() => this.allData());

  readonly registroInicio = computed(() => {
    if (this.totalRegistros() === 0) return 0;
    return (this.currentPage() - 1) * this.pageSize() + 1;
  });

  readonly registroFin = computed(() => {
    return Math.min(
      this.currentPage() * this.pageSize(),
      this.totalRegistros()
    );
  });


  // === Computed: Tipo de Cita ===
  // Legacy: configContactosAdicionales() + isTipoCampo()

  /**
   * Es tipo CAMPO (17) o CAMPO COMPLEMENTARIO (42)
   * Legacy: TiposDeCita.CAMPO, TiposDeCita.CAMPO_COMPLEMENTARIO
   */
  readonly esTipoCampo = computed(() =>
    esTipoCitaCampo(this.selectedProgramacion())
  );

  /**
   * Es tipo INFORME (18) o INFORME COMPLEMENTARIO (43)
   * Legacy: TiposDeCita.INFORME, TiposDeCita.INFORME_COMPLEMENTARIO
   */
  readonly esTipoInforme = computed(() =>
    esTipoCitaInforme(this.selectedProgramacion())
  );

  // === Computed: Mostrar sección transporte ===
  readonly mostrarSeccionTransporte = computed(() => {
    // Solo para CAMPO y no virtual
    return this.esTipoCampo();
  });

  // === Computed: Municipios precargados desde programación ===
  // Legacy: CreateOrUpdateCumplimientoActivity.java líneas 178-179
  // getView().setTpscMunicipioOrigenDto(placeOrigen.getProgramServiciosDto().getNmconsec_orden_serv_detalle().getCdmunicipio_origen());
  // getView().setTpscMunicipioEntregaDto(placeOrigen.getProgramServiciosDto().getNmconsec_orden_serv_detalle().getCdmunicipio_entrega());

  /**
   * Municipio de origen precargado desde la programación seleccionada.
   * cdmunicipio: Código DANE para FK en BD
   * cdsura: Código SURA para cálculo de tarifas de transporte
   */
  readonly municipioOrigenInicial = computed(() => {
    const prog = this.selectedProgramacion();
    if (!prog?.cdmunicipioOrigen && !prog?.cdsuraOrigen) return null;
    return {
      cdmunicipio: prog.cdmunicipioOrigen || '', // FK usa CDMUNICIPIO
      cdsura: prog.cdsuraOrigen || '', // Tarifas usan CDSURA
      dsmunicipio: prog.dsmunicipioOrigen || prog.cdmunicipioOrigen || ''
    };
  });

  /**
   * Municipio de entrega precargado desde la programación seleccionada.
   * cdmunicipio: Código DANE para FK en BD
   * cdsura: Código SURA para cálculo de tarifas de transporte
   */
  readonly municipioEntregaInicial = computed(() => {
    const prog = this.selectedProgramacion();
    if (!prog?.cdmunicipioEntrega && !prog?.cdsuraEntrega) return null;
    return {
      cdmunicipio: prog.cdmunicipioEntrega || '', // FK usa CDMUNICIPIO
      cdsura: prog.cdsuraEntrega || '', // Tarifas usan CDSURA
      dsmunicipio: prog.dsmunicipioEntrega || prog.cdmunicipioEntrega || ''
    };
  });

  // === Estado Contactos Adicionales ===
  readonly contactosAdicionales = signal<
    { dsnombre: string; dsemail: string }[]
  >([]);
  readonly mostrarContactosDialog = signal(false);

  // =========================================================================
  // MÉTODOS PÚBLICOS - Listado
  // =========================================================================

  /**
   * Cargar catálogos para dropdowns y precargar datos del asesor y aseguradora
   * Replica el comportamiento del legacy initBuscadores() + initListBox() + searchDefaultClient()
   */
  async cargarCatalogos(): Promise<void> {
    this.loading.set(true);
    try {
      const oficinas = await this.oficinaRepo.listarActivas();
      this.oficinas.set(oficinas);

      // Legacy: initBuscadores() - Precargar consultor si es asesor
      await this.precargarConsultorSesion();

      // Legacy: ApplicationCommonData.searchDefaultClient() - Precargar aseguradora por defecto
      await this.cargarAseguradoraDefault();
    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al cargar catálogos'
      });
    } finally {
      this.loading.set(false);
    }
  }

  /**
   * Legacy: initBuscadores()
   * Si el usuario es tipo ASESOR, precargar sus datos en el filtro de Consultor
   * y ocultar la lupa (no puede buscar otro asesor).
   *
   * Compara cdtipoEmpleado con ConstantsEJB.TipoEmpleado.ASESOR ("A")
   */
  private async precargarConsultorSesion(): Promise<void> {
    try {
      const sesion = await this.usuarioRepo.obtenerDatosSesion();
      const esAsesorActual = sesion.cdtipoEmpleado === TIPO_EMPLEADO.ASESOR;

      if (esAsesorActual) {
        const nombreCompleto = [sesion.primerNombre, sesion.primerApellido]
          .filter(Boolean)
          .join(' ');

        const consultorData: CampoSeleccionadoUI = {
          id: sesion.nmconsecutivo,
          codigo: sesion.numeroIdentificacion,
          descripcion: nombreCompleto,
          nombreComercial: nombreCompleto,
          nombre1: sesion.primerNombre,
          nombre2: sesion.segundoNombre ?? '',
          apellido1: sesion.primerApellido,
          apellido2: sesion.segundoApellido ?? ''
        };

        this.consultorPrecargado.set(consultorData);
        this.esAsesor.set(true);
        this.filtro.update((f) => ({ ...f, consultor: consultorData }));
      } else {
        this.esAsesor.set(false);
        this.consultorPrecargado.set(null);
      }
    } catch (error) {
      if (!environment.production)
        console.error('[Facade] Error obteniendo sesión de usuario:', error);
      // Fallback: usuario NO asesor (puede buscar consultores)
      this.esAsesor.set(false);
      this.consultorPrecargado.set(null);
    }
  }

  /**
   * Legacy: ApplicationCommonData.searchDefaultClient()
   * Carga la aseguradora por defecto desde el contrato dedicado de backend
   * y la precarga en el filtro de Aseguradora.
   */
  private async cargarAseguradoraDefault(): Promise<void> {
    try {
      const aseguradora = await this.aseguradoraRepo.obtenerAseguradoraDefault();
      if (!aseguradora) return;

      // Crear objeto para el filtro
      const aseguradoraData: CampoSeleccionadoUI = {
        id: aseguradora.nmconsecutivo,
        codigo: aseguradora.numeroIdentificacion || '',
        descripcion:
          aseguradora.nombreComercial || aseguradora.primerNombre || '',
        nombreComercial:
          aseguradora.nombreComercial || aseguradora.primerNombre || ''
      };

      // Guardar para preservar en limpiarFiltros()
      this.aseguradoraPrecargada.set(aseguradoraData);

      // Precargar en el filtro
      this.filtro.update((f) => ({ ...f, aseguradora: aseguradoraData }));
    } catch (error) {
      // Si falla, continuar sin aseguradora por defecto
      if (!environment.production)
        console.warn('No se pudo cargar la aseguradora por defecto:', error);
    }
  }

  /**
   * Actualizar filtro
   */
  actualizarFiltro(campo: keyof FiltrosUI, valor: any): void {
    this.filtro.update((f) => ({ ...f, [campo]: valor }));
  }

  /**
   * Buscar programaciones pendientes
   *
   * Mapea los filtros de UI (con objetos CampoSeleccionado) a filtros de API (strings)
   *
   * Validaciones Legacy:
   * - validarFiltrosProgramacion(): Al menos un filtro debe estar diligenciado
   * - validarFechas(): Si hay una fecha, ambas son requeridas y final >= inicial
   */
  async buscar(): Promise<void> {
    const filtroUI = this.filtro();

    // Legacy: validarFiltrosProgramacion() - Al menos un filtro debe estar diligenciado
    if (!this.validarFiltrosProgramacion(filtroUI)) {
      return;
    }

    // Legacy: validarFechas() - Si hay una fecha, ambas son requeridas y final >= inicial
    if (!this.validarFechas()) {
      return;
    }

    this.loading.set(true);

    // Mapear filtros de UI a filtros de API
    // Legacy: Todos usan nmconsecutivo (FK) excepto unidad administrativa que usa código STRING
    const filtroApi: CumplimientoFiltro = {
      ordenServicio: filtroUI.ordenServicio,
      cdoficina: filtroUI.cdoficina,
      // Legacy: nmcons_unidad_administrativa.nmcons_unidad_administrativa.cdunidad_administrativa (código STRING)
      cdunidadAdministrativa: filtroUI.unidadAdministrativa?.codigo,
      // Legacy: nmconsec_homologacion_servicio.nmconsecutivo
      nmconsecutivoHomologacion: filtroUI.tarea?.id ?? undefined,
      // Legacy: nmconsecutivo_asesor.nmconsecutivo
      // Si viene del dialog → cdpersonaAsesor (persona.cdpersona), si viene de precarga → nmconsecutivoAsesor (empleado.nmconsecutivo)
      nmconsecutivoAsesor: filtroUI.consultor?.esDesdeDialog
        ? undefined
        : (filtroUI.consultor?.id ?? undefined),
      cdpersonaAsesor: filtroUI.consultor?.esDesdeDialog
        ? (filtroUI.consultor?.id ?? undefined)
        : undefined,
      // Legacy: nmconsecutivo_cliente.nmconsecutivo
      nmconsecutivoCliente: filtroUI.aseguradora?.id ?? undefined,
      // Legacy: nmconsecutivo_beneficiario.nmconsecutivo
      nmconsecutivoBeneficiario: filtroUI.cliente?.id ?? undefined,
      // Legacy: nmconsec_benef_ct.nmconsecutivo
      nmconsecutivoCentroTrabajo: filtroUI.centroTrabajo?.id ?? undefined,
      // Fechas - Legacy: Trunc(feprogramacion) >= / <=
      fechaInicial: this.fechaInicial()
        ? dateToString(this.fechaInicial()!)
        : undefined,
      fechaFinal: this.fechaFinal()
        ? dateToString(this.fechaFinal()!)
        : undefined
    };

    // Guardar filtro para reutilizar en paginación
    this.filtroApiActual = filtroApi;

    try {
      // Paginación Keyset/Cursor: primera página (cursor null).
      const size = this.pageSize();
      const response =
        await this.cumplimientoRepo.listarProgramacionesPendientes(
          filtroApi,
          null,
          size,
          'NEXT'
        );
      this.allData.set(response.content);
      this.totalElements.set(response.totalElements ?? response.content.length);
      this.nextCursorActual = response.nextCursor ?? null;
      this.previousCursorActual = response.previousCursor ?? null;
      await this.actualizarConteoRiesgosPotencialesDesdeFiltros();
      this.hasNext.set(!!response.hasNext);
      this.hasPrevious.set(!!response.hasPrevious);
      // Búsqueda nueva: siempre página 1, solo tras la respuesta exitosa
      this.currentPage.set(1);
      this.busquedaRealizada.set(true);
      this.loading.set(false);

      if (response.content.length === 0 && this.totalElements() === 0) {
        // Legacy: busquedaNoArrojoResultado
        this.messageService.add({
          severity: 'warn',
          summary: 'Sin resultados',
          detail: 'No se recuperó información con los parámetros ingresados.'
        });
      }
    } catch (err: any) {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: err.error?.message || 'Error al consultar programaciones'
      });
    }
  }

  /**
   * Legacy: validarFiltrosProgramacion()
   * Al menos un filtro debe estar diligenciado para poder buscar.
   * Mensaje: "Debe diligenciar al menos uno de los filtros"
   */
  private validarFiltrosProgramacion(filtro: FiltrosUI): boolean {
    const tieneFiltro =
      !!filtro.ordenServicio?.trim() ||
      !!filtro.cdoficina ||
      !!filtro.unidadAdministrativa?.id ||
      !!filtro.consultor?.id ||
      !!filtro.aseguradora?.id ||
      !!filtro.cliente?.id ||
      !!filtro.tarea?.id ||
      !!filtro.centroTrabajo?.id ||
      (this.fechaInicial() !== null && this.fechaFinal() !== null);

    if (!tieneFiltro) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'Debe diligenciar al menos uno de los filtros'
      });
      return false;
    }
    return true;
  }

  /**
   * Legacy: validarFechas()
   * Si se ingresa una fecha, la otra es obligatoria.
   * Además, fecha final debe ser >= fecha inicial.
   * Mensaje: "La fecha final esperada debe ser mayor que la fecha inicial"
   */
  private validarFechas(): boolean {
    const feInicial = this.fechaInicial();
    const feFinal = this.fechaFinal();

    // Si no hay ninguna fecha, está OK
    if (feInicial === null && feFinal === null) {
      return true;
    }

    // Si hay una pero falta la otra
    if (feInicial === null || feFinal === null) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'La fecha final esperada debe ser mayor que la fecha inicial'
      });
      return false;
    }

    // Ambas fechas están, validar que final >= inicial
    if (feInicial > feFinal) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validación',
        detail: 'La fecha final esperada debe ser mayor que la fecha inicial'
      });
      return false;
    }

    return true;
  }

  /**
   * Limpiar filtros y resultados
   * Legacy:
   * - Si es asesor, mantiene el consultor precargado (no se puede limpiar)
   * - Mantiene la aseguradora por defecto (ApplicationCommonData.getDefaultClient())
   */
  limpiarFiltros(): void {
    // Preservar valores precargados
    const filtroInicial: FiltrosUI = {};

    // Si es asesor, preservar el consultor precargado
    if (this.esAsesor() && this.consultorPrecargado()) {
      filtroInicial.consultor = this.consultorPrecargado()!;
    }

    // Preservar la aseguradora por defecto
    if (this.aseguradoraPrecargada()) {
      filtroInicial.aseguradora = this.aseguradoraPrecargada()!;
    }

    this.filtro.set(filtroInicial);
    this.fechaInicial.set(null);
    this.fechaFinal.set(null);
    this.allData.set([]);
    this.riesgoConteoTotal.set(0);
    this.riesgoConteoAbiertos.set(0);
    this.riesgoConteoCerrados.set(0);
    this.busquedaRealizada.set(false);
    this.currentPage.set(1);
    this.hasNext.set(false);
    this.hasPrevious.set(false);
    this.nextCursorActual = null;
    this.previousCursorActual = null;
  }

  /**
   * Consulta conteo agregado de riesgos potenciales (total/abiertos/cerrados)
   * usando el contexto de filtros actual del listado.
   */
  private async actualizarConteoRiesgosPotencialesDesdeFiltros(): Promise<void> {
    const filtroUI = this.filtro();
    const nmconsecutivoOficina = this.parsePositiveInt(filtroUI.cdoficina);
    const filtroConteo: RiesgoPotencialFiltro = {
      nmconsecutivoOficina: nmconsecutivoOficina ?? undefined,
      nmconsecutivoAsesor: filtroUI.consultor?.id ?? undefined,
      nmconsecutivoBenef: filtroUI.cliente?.id ?? undefined
    };

    this.riesgoConteoLoading.set(true);
    try {
      const conteo = await this.cumplimientoRepo.contarRiesgosPotenciales(
        filtroConteo
      );
      this.riesgoConteoTotal.set(conteo.total ?? 0);
      this.riesgoConteoAbiertos.set(conteo.abiertos ?? 0);
      this.riesgoConteoCerrados.set(conteo.cerrados ?? 0);
    } catch (error) {
      this.riesgoConteoTotal.set(0);
      this.riesgoConteoAbiertos.set(0);
      this.riesgoConteoCerrados.set(0);
      if (!environment.production) {
        console.warn('[Facade] Error consultando conteo de riesgos:', error);
      }
    } finally {
      this.riesgoConteoLoading.set(false);
    }
  }

  private parsePositiveInt(value: unknown): number | null {
    if (value === null || value === undefined) {
      return null;
    }
    const parsed = Number.parseInt(String(value).trim(), 10);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null;
    }
    return parsed;
  }

  // =========================================================================
  // MÉTODOS PÚBLICOS - Diálogos de Búsqueda (Lupas)
  // =========================================================================

  // --- Unidad Administrativa ---
  abrirDialogUnidadAdmin(): void {
    this.mostrarDialogUnidadAdmin.set(true);
  }
  cerrarDialogUnidadAdmin(): void {
    this.mostrarDialogUnidadAdmin.set(false);
  }
  seleccionarUnidadAdmin(item: any): void {
    // El diálogo emite: { nmconsecutivo, cdunidadAdministrativa, dsunidadAdministrativa }
    this.filtro.update((f) => ({
      ...f,
      unidadAdministrativa: {
        id: item.nmconsecutivo,
        codigo: item.cdunidadAdministrativa,
        descripcion: item.dsunidadAdministrativa
      }
    }));
    this.cerrarDialogUnidadAdmin();
  }

  // --- Tarea ---
  abrirDialogTarea(): void {
    this.mostrarDialogTarea.set(true);
  }
  cerrarDialogTarea(): void {
    this.mostrarDialogTarea.set(false);
  }
  seleccionarTarea(item: any): void {
    this.filtro.update((f) => ({
      ...f,
      tarea: {
        id: item.nmconsecutivo,
        codigo: item.cdservicio,
        descripcion: item.descripcionCompleta || item.dstarea
      }
    }));
    this.cerrarDialogTarea();
  }

  // --- Consultor (Profesional/Asesor) ---
  // Legacy: IdentificationViewImpl con LOOKUP_ADVISORS
  // Layout: Identificación + Nombre + nombre1/2 + apellido1/2
  // Si es asesor: se precarga desde sesión y se oculta lupa
  abrirDialogConsultor(): void {
    this.mostrarDialogConsultor.set(true);
  }
  cerrarDialogConsultor(): void {
    this.mostrarDialogConsultor.set(false);
  }

  seleccionarConsultorDesdeDialog(asesor: AsesorSeleccionado): void {
    // Legacy: el filtro usa NMCONSECUTIVO de TPSC_EMPLEADOS (emp.NMCONSECUTIVO = :nmconsecutivoAsesor)
    // Si nmconsecutivo está disponible (siempre desde el diálogo), usarlo como id para que
    // se envíe como nmconsecutivoAsesor al backend. Si solo hay cdpersona, usar esDesdeDialog.
    const tieneNmconsecutivo = asesor.nmconsecutivo != null;
    this.filtro.update((f) => ({
      ...f,
      consultor: {
        id: tieneNmconsecutivo
          ? asesor.nmconsecutivo!
          : (asesor.cdpersona ?? null),
        codigo: asesor.nmidentificacion || '',
        descripcion: [
          asesor.dsnombre1,
          asesor.dsnombre2,
          asesor.dsapellido1,
          asesor.dsapellido2
        ]
          .filter(Boolean)
          .join(' '),
        nombreComercial: asesor.dsnombreComercial || '',
        tipoIdentificacion: asesor.cdtipoIdentificacion || '',
        nombre1: asesor.dsnombre1 || '',
        nombre2: asesor.dsnombre2 || '',
        apellido1: asesor.dsapellido1 || '',
        apellido2: asesor.dsapellido2 || '',
        esDesdeDialog: !tieneNmconsecutivo
      }
    }));
    this.cerrarDialogConsultor();
  }

  seleccionarConsultor(item: any): void {
    this.filtro.update((f) => ({
      ...f,
      consultor: {
        id: item.nmconsecutivo,
        codigo: item.dni,
        descripcion: item.dsnombre || '',
        nombreComercial: item.dsnombre || '',
        tipoIdentificacion:
          item.tipoIdentificacion || item.cdtipoIdentificacion || '',
        nombre1: item.nombre1 || item.dsnombre1 || '',
        nombre2: item.nombre2 || item.dsnombre2 || '',
        apellido1: item.apellido1 || item.dsapellido1 || '',
        apellido2: item.apellido2 || item.dsapellido2 || ''
      }
    }));
    this.cerrarDialogConsultor();
  }

  // --- Aseguradora ---
  // Legacy: IdentificationViewImpl con LOOKUP_CLIENTS
  // Solo muestra: Identificación + Nombre
  abrirDialogAseguradora(): void {
    this.mostrarDialogAseguradora.set(true);
  }
  cerrarDialogAseguradora(): void {
    this.mostrarDialogAseguradora.set(false);
  }
  seleccionarAseguradora(item: any): void {
    this.filtro.update((f) => ({
      ...f,
      aseguradora: {
        id: item.nmconsecutivo || item.cdpersona,
        codigo:
          item.numeroIdentificacion ||
          item.tipoIdentificacion + item.numeroIdentificacion,
        descripcion: item.nombreComercial || '',
        nombreComercial: item.nombreComercial || ''
      }
    }));
    this.cerrarDialogAseguradora();
  }

  // --- Cliente (Beneficiario) ---
  abrirDialogCliente(): void {
    this.mostrarDialogCliente.set(true);
  }
  cerrarDialogCliente(): void {
    this.mostrarDialogCliente.set(false);
  }
  seleccionarCliente(item: any): void {
    this.filtro.update((f) => ({
      ...f,
      cliente: {
        id: item.nmconsecutivo,
        codigo: item.numeroIdentificacion,
        descripcion:
          item.nombreComercial ||
          `${item.numeroIdentificacion} - ${item.nombreComercial}`
      },
      // Limpiar centro de trabajo cuando cambia el cliente
      centroTrabajo: undefined
    }));
    this.cerrarDialogCliente();
  }

  // --- Centro de Trabajo ---
  // Legacy: UtilidadesBuscadores.buscarCentroTrabajo() - Cliente es OPCIONAL
  abrirDialogCentroTrabajo(): void {
    this.mostrarDialogCentroTrabajo.set(true);
  }
  cerrarDialogCentroTrabajo(): void {
    this.mostrarDialogCentroTrabajo.set(false);
  }
  seleccionarCentroTrabajo(item: any): void {
    this.filtro.update((f) => ({
      ...f,
      centroTrabajo: {
        id: item.nmconsecutivo,
        codigo: item.cdcentroTrabajo,
        descripcion: item.descripcionCompleta || item.dscentroTrabajo
      }
    }));
    this.cerrarDialogCentroTrabajo();
  }

  // =========================================================================
  // MÉTODOS PÚBLICOS - Paginación (del servidor)
  // =========================================================================

  irPrimeraPagina(): void {
    if (this.loading()) return;
    void this.cargarPaginaActual(null, 'NEXT', 1);
  }
  /**
   * Salta a la última página. Soportado con Keyset/Cursor porque el backend
   * acepta direction=PREVIOUS sin cursor para este endpoint (ver
   * contrato-paginacion-keyset-lote4.md, apéndice "Lote 4 completado").
   */
  irUltimaPagina(): void {
    if (this.loading() || this.totalRegistros() <= 0) return;
    const size = this.pageSize();
    void this.cargarPaginaActual(
      null,
      'LAST',
      size > 0 ? Math.max(1, Math.ceil(this.totalRegistros() / size)) : 1
    );
  }
  irPaginaAnterior(): void {
    if (this.loading() || !this.hasPrevious()) return;
    void this.cargarPaginaActual(
      this.previousCursorActual,
      'PREVIOUS',
      Math.max(1, this.currentPage() - 1)
    );
  }
  irPaginaSiguiente(): void {
    if (this.loading() || !this.hasNext()) return;
    void this.cargarPaginaActual(
      this.nextCursorActual,
      'NEXT',
      this.currentPage() + 1
    );
  }
  cambiarTamanioPagina(size: number): void {
    if (this.loading()) return;
    this.pageSize.set(size);
    // Sin búsqueda previa no hay nada que recargar: solo se reinicia la página
    if (!this.busquedaRealizada()) {
      this.currentPage.set(1);
      return;
    }
    void this.cargarPaginaActual(null, 'NEXT', 1);
  }

  /**
   * Carga una página del servidor por cursor (keyset, ver skill
   * acceso-datos-oracle §7) usando el filtro guardado. Recibe la página
   * destino: currentPage y los flags solo cambian tras la respuesta exitosa;
   * si la petición falla se conserva el estado anterior.
   */
  private async cargarPaginaActual(
    cursor: string | null,
    direction: 'NEXT' | 'PREVIOUS' | 'LAST',
    paginaDestino: number
  ): Promise<void> {
    if (!this.busquedaRealizada()) return;

    this.loading.set(true);
    try {
      const size = this.pageSize();
      const response =
        await this.cumplimientoRepo.listarProgramacionesPendientes(
          this.filtroApiActual,
          cursor,
          size,
          direction
        );
      this.allData.set(response.content);
      this.nextCursorActual = response.nextCursor ?? null;
      this.previousCursorActual = response.previousCursor ?? null;
      this.hasNext.set(!!response.hasNext);
      this.hasPrevious.set(!!response.hasPrevious);

      if (direction === 'LAST') {
        const total = response.totalElements ?? this.totalElements();
        this.currentPage.set(size > 0 && total > 0 ? Math.ceil(total / size) : 1);
      } else {
        this.currentPage.set(paginaDestino);
      }
      // No actualizamos totalElements porque no cambia entre páginas
      this.loading.set(false);
    } catch (err: any) {
      this.loading.set(false);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: err.error?.message || 'Error al cargar página'
      });
    }
  }

  // =========================================================================
  // MÉTODOS PÚBLICOS - Formulario Cumplimiento
  // =========================================================================

  /**
   * Abrir formulario para registrar cumplimiento
   */
  async abrirFormularioCumplimiento(
    programacion: ProgramacionPendiente
  ): Promise<void> {
    this.formLoading.set(true);
    try {
      const detalle = await this.cumplimientoRepo.obtenerProgramacion(
        programacion.nmconsecutivo
      );
      if (detalle) {
        this.selectedProgramacion.set(detalle);
      } else {
        this.selectedProgramacion.set(programacion);
        this.messageService.add({
          severity: 'warn',
          summary: 'Aviso',
          detail:
            'No fue posible cargar el detalle completo de la programacion. Se mostraran datos parciales.'
        });
      }
    } catch {
      this.selectedProgramacion.set(programacion);
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail:
          'No fue posible cargar el detalle completo de la programacion. Se mostraran datos parciales.'
      });
    } finally {
      this.formLoading.set(false);
    }

    this.saldoActual.set(0);
    this.dialogFormVisible.set(true);
    this.municipioOrigen.set(null);
    this.municipioDestino.set(null);
    this.contactosAdicionales.set([]);
    this.mostrarContactosDialog.set(false);
    this.mostrarBotonDescargarActa.set(false);
    this.nmconsecutivoCumplimientoDescargable = null;

    // Legacy: CreateOrUpdateCumplimientoActivity.loadParameters() + loadParametersHoliDays()
    // Cargar configuración de tardío ANTES de verificar
    await this.cargarConfiguracionTardio();

    // Verificar si cumplimiento es tardío
    // Legacy: CreateOrUpdateCumplimientoViewImpl.cumplimientoTardio()
    this.verificarCumplimientoTardio(new Date());

    // Consultar saldo actualizado
    const programacionActual = this.selectedProgramacion();
    if (programacionActual?.nmconsecOrdenServDetalle) {
      await this.consultarSaldoActualizado(
        programacionActual.nmconsecOrdenServDetalle
      );
    }
  }

  /**
   * Abre el formulario de cumplimiento desde un contexto externo
   * (ej. Informacion de Tareas) resolviendo primero la programacion completa.
   */
  async abrirFormularioCumplimientoPorConsecutivo(
    nmconsecutivoProgramacion: number
  ): Promise<void> {
    if (
      !Number.isFinite(nmconsecutivoProgramacion) ||
      nmconsecutivoProgramacion <= 0
    ) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'No se recibió una programación válida para modificar.'
      });
      return;
    }

    this.formLoading.set(true);
    try {
      const programacion = await this.cumplimientoRepo.obtenerProgramacion(
        nmconsecutivoProgramacion
      );
      await this.abrirFormularioCumplimiento(programacion);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          'No fue posible cargar la programación seleccionada para modificar cumplimiento.'
      });
    } finally {
      this.formLoading.set(false);
    }
  }

  setOrigenInformacionTareas(origenInformacionTareas: boolean): void {
    this.origenInformacionTareas.set(origenInformacionTareas);
  }

  /**
   * Cerrar formulario
   */
  cerrarFormulario(): void {
    this.dialogFormVisible.set(false);
    this.selectedProgramacion.set(null);
    this.contactosAdicionales.set([]);
    this.mostrarContactosDialog.set(false);
    this.actaError.set(null);
    this.actaGenerando.set(false);
    this.mostrarBotonReintentar.set(false);
    this.mostrarBotonDescargarActa.set(false);
    this.descargandoActa.set(false);
    this.nmconsecutivoCumplimientoGuardado = null;
    this.nmconsecutivoCumplimientoDescargable = null;
    this.ultimaDatosActa = null;
    this.soloActaModo.set(false);
    this.nmconsecutivoCumplimientoSoloActa = null;
    this._programacionOriginalPreSoloActa = null;
  }

  /**
   * Vuelve del modo soloActa al historial, sin cerrar el formulario de cumplimiento.
   * Restaura la programación original y muestra el historial encima.
   */
  volverDesdesoloActa(): void {
    this.soloActaModo.set(false);
    this.actaError.set(null);
    this.actaGenerando.set(false);
    this.mostrarBotonDescargarActa.set(false);
    this.nmconsecutivoCumplimientoDescargable = null;
    this.nmconsecutivoCumplimientoSoloActa = null;

    // Primero abrir historial (tapa el formulario visualmente)
    this.mostrarHistorialDialog.set(true);

    // Luego restaurar programación (puede disparar efectos secundarios del form, pero está tapado)
    if (this._programacionOriginalPreSoloActa) {
      this.selectedProgramacion.set(this._programacionOriginalPreSoloActa);
      this._programacionOriginalPreSoloActa = null;
    }
  }

  /**
   * Abrir URL de Alerta Temprana
   *
   * Legacy: CreateOrUpdateCumplimientoViewImpl.java → saNuevoRp.onClickNewRp()
   * Abre URL externa (GeneralParameters.URL_ALERTA_TEM) con parámetros:
   *   - TIPO_DOCUMENTO: Tipo documento del beneficiario
   *   - NUMERO_DOCUMENTO: DNI del beneficiario
   *   - FUENTE: "GIROP"
   *   - FUNCION: "GIROP"
   */
  abrirUrlAlertaTemprana(): void {
    void abrirAlertaTemprana(this.selectedProgramacion(), this.messageService);
  }

  /**
   * Ver historial de cumplimientos de la programación seleccionada.
   * Legacy: saHistProg → showModalHistProg()
   *
   * Muestra un modal con el historial de cumplimientos para la programación seleccionada.
   */
  async verHistorialCumplimientos(): Promise<void> {
    const prog = this.selectedProgramacion();
    if (!prog) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'No hay una programación seleccionada'
      });
      return;
    }

    this.loadingHistorial.set(true);
    this.mostrarHistorialDialog.set(true);

    try {
      // Cargar cumplimientos de la programación
      const cumplimientos =
        await this.cumplimientoRepo.listarHistorialCumplimientos(
          prog.nmconsecutivo
        );
      this.historialCumplimientos.set(cumplimientos);

      // Cargar configuración de contratos exentos si no se ha cargado
      if (this.contratosCumpl().length === 0) {
        await this.cargarConfiguracionHistorial();
      }
    } catch (error) {
      if (!environment.production)
        console.error('Error cargando historial:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo cargar el historial de cumplimientos'
      });
      this.mostrarHistorialDialog.set(false);
    } finally {
      this.loadingHistorial.set(false);
    }
  }

  /**
   * Cerrar modal de historial.
   * No limpia datos si estamos en flujo soloActa (para poder volver con "Regresar").
   */
  cerrarHistorialDialog(): void {
    this.mostrarHistorialDialog.set(false);
    this.actaRecienAsociada.set(false);
    // Solo limpiar datos si NO estamos en transición a soloActa
    if (!this.soloActaModo()) {
      this.historialCumplimientos.set([]);
    }
  }

  /**
   * Recarga el historial manualmente (botón "Recargar" tras asociar acta).
   */
  async recargarHistorial(): Promise<void> {
    this.actaRecienAsociada.set(false);
    const prog = this.selectedProgramacion();
    if (!prog) return;

    this.loadingHistorial.set(true);
    try {
      const cumplimientos =
        await this.cumplimientoRepo.listarHistorialCumplimientos(
          prog.nmconsecutivo
        );
      this.historialCumplimientos.set(cumplimientos);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudo recargar el historial.'
      });
    } finally {
      this.loadingHistorial.set(false);
    }
  }

  /**
   * Abre el formulario de cumplimiento en modo soloActa desde el historial.
   *
   * El formulario YA está abierto (el historial se abre desde dentro del formulario),
   * así que solo cambiamos los datos y el modo — sin cerrar/reabrir diálogos.
   */
  async abrirAsociarActaDesdeHistorial(
    cumplimiento: HistorialCumplimiento
  ): Promise<void> {
    this.nmconsecutivoCumplimientoSoloActa = cumplimiento.nmconsecutivo;
    this._programacionOriginalPreSoloActa = this.selectedProgramacion();

    // Cerrar solo el historial — el formulario sigue abierto detrás
    this.mostrarHistorialDialog.set(false);

    // Cambiar a modo soloActa (oculta secciones de cumplimiento, muestra solo acta)
    this.soloActaModo.set(true);
    this.formLoading.set(true);
    this.mostrarBotonDescargarActa.set(false);
    this.nmconsecutivoCumplimientoDescargable = null;

    try {
      // Cargar la programación completa para que datos-programacion se actualice
      const programacion = await this.cumplimientoRepo.obtenerProgramacion(
        cumplimiento.nmconsecutivoProgramacion
      );

      if (!programacion.dsclasificacion && cumplimiento.dsclasificacion) {
        programacion.dsclasificacion = cumplimiento.dsclasificacion;
      }
      if (!programacion.cdcontratoBeneficiario && cumplimiento.cdcontrato) {
        programacion.cdcontratoBeneficiario = cumplimiento.cdcontrato;
      }

      this.selectedProgramacion.set(programacion);
    } catch {
      // Fallback con datos del historial
      const programacionPendiente: ProgramacionPendiente = {
        nmconsecutivo: cumplimiento.nmconsecutivoProgramacion,
        nmconsecOrdenServDetalle: 0,
        nmconsecutivoManualOrden: '',
        nmconsecutivoTipoCita: cumplimiento.nmconsecutivoTipoCita,
        dsnombreContacto: cumplimiento.dsnombreContacto,
        dsemailContacto: cumplimiento.dsemailContacto,
        dsclasificacion: cumplimiento.dsclasificacion,
        cdcontratoBeneficiario: cumplimiento.cdcontrato
      };
      this.selectedProgramacion.set(programacionPendiente);
    } finally {
      this.formLoading.set(false);
    }
  }

  /**
   * Handler para el output (reintentar) del formulario-cumplimiento en modo soloActa desde historial.
   * Usa el mismo endpoint reintentarGenerarActa del repositorio.
   */
  async onReintentarActaDesdeHistorial(
    datosActa: DatosActaRequest
  ): Promise<void> {
    if (this.actaGenerando()) {
      return;
    }

    const nmconsecutivo = this.nmconsecutivoCumplimientoSoloActa;
    if (!nmconsecutivo) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se encontró el consecutivo del cumplimiento.'
      });
      return;
    }

    this.actaGenerando.set(true);
    this.actaError.set(null);

    try {
      const result = await this.cumplimientoRepo.reintentarGenerarActa(
        nmconsecutivo,
        datosActa
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Acta asociada',
        detail: `El acta ${result.idActa360} fue generada y asociada al cumplimiento.`,
        life: 6000
      });
      this.nmconsecutivoCumplimientoDescargable = nmconsecutivo;
      this.mostrarBotonDescargarActa.set(true);
    } catch (err: unknown) {
      const e = err as {
        error?: { message?: string; detail?: string };
        message?: string;
      };
      const detail =
        e?.error?.detail ||
        e?.error?.message ||
        e?.message ||
        'No fue posible asociar el acta.';
      this.actaError.set(detail);
      this.messageService.add({
        severity: 'error',
        summary: 'Error al asociar acta',
        detail,
        life: 8000
      });
    } finally {
      this.actaGenerando.set(false);
    }
  }

  /**
   * Cierra el formulario en modo soloActa tras éxito y vuelve al historial refrescado.
   */
  private async cerrarFormularioSoloActa(): Promise<void> {
    this.soloActaModo.set(false);
    this.nmconsecutivoCumplimientoSoloActa = null;
    this.actaError.set(null);
    this.actaGenerando.set(false);
    this.actaRecienAsociada.set(true);

    // Restaurar programación original
    if (this._programacionOriginalPreSoloActa) {
      this.selectedProgramacion.set(this._programacionOriginalPreSoloActa);
      this._programacionOriginalPreSoloActa = null;
    }
    // Recargar historial para mostrar el nuevo estado del acta
    await this.verHistorialCumplimientos();
  }

  /**
   * Abrir modal de observaciones/recomendaciones.
   * Legacy: ListarCumplimientosViewImpl.java líneas 378-416
   */
  verObservaciones(cumplimiento: HistorialCumplimiento): void {
    this.cumplimientoSeleccionadoObs.set(cumplimiento);
    this.mostrarObservacionesDialog.set(true);
  }

  /**
   * Cerrar modal de observaciones.
   */
  cerrarObservacionesDialog(): void {
    this.mostrarObservacionesDialog.set(false);
    this.cumplimientoSeleccionadoObs.set(null);
  }

  /**
   * Carga configuración para el historial (CONTRATOS_CUMPL).
   * Legacy: GeneralParameters.getParameterValue()
   */
  private async cargarConfiguracionHistorial(): Promise<void> {
    try {
      const configCumplimiento =
        await this.cumplimientoRepo.obtenerConfiguracionCumplimiento();
      this.contratosCumpl.set(configCumplimiento.contratosCumpl ?? []);
    } catch (error) {
      if (!environment.production)
        console.error('Error cargando configuración historial:', error);
    }
  }

  /**
   * Guardar contactos adicionales
   * Legacy: AdicionarContactosActivity.buildListDto()
   */
  guardarContactosAdicionales(
    contactos: { dsnombre: string; dsemail: string }[]
  ): void {
    this.contactosAdicionales.set(contactos);
    this.mostrarContactosDialog.set(false);
  }

  private async actualizarContactoCumplimientoSiAplica(
    nmconsecutivoCumplimiento: number,
    request: CumplimientoRequest
  ): Promise<void> {
    const nombreContacto = (request.nombreContacto || '').trim();
    const emailContacto = (request.correoContacto || '').trim();

    // Legacy: el contacto se guarda con el cumplimiento (no hay segunda llamada) y el popup
    // "Contacto / Editar" (el que usa este PUT) exige nombre Y correo. El formulario ya exige ambos para tipo
    // campo; si aun así llega un contacto a medias, el POST ya lo guardó: llamar al PUT solo produciría un
    // 400 y un aviso falso.
    if (!nombreContacto || !emailContacto) {
      return;
    }

    try {
      await this.cumplimientoRepo.actualizarContactoCumplimiento(
        nmconsecutivoCumplimiento,
        nombreContacto,
        emailContacto
      );
    } catch (error) {
      if (!environment.production) {
        console.warn(
          '[Facade] No fue posible actualizar el contacto del cumplimiento:',
          error
        );
      }
      this.messageService.add({
        severity: 'warn',
        summary: 'Contacto pendiente de actualizacion',
        detail:
          'La orientacion fue registrada, pero no se logro actualizar el contacto del cumplimiento.',
        life: 8000
      });
    }
  }

  /**
   * Abrir diálogo de contactos adicionales
   */
  abrirContactosDialog(): void {
    this.mostrarContactosDialog.set(true);
  }

  /**
   * Consultar saldo actualizado desde el backend
   */
  private async consultarSaldoActualizado(
    nmconsecOrdenServDetalle: number
  ): Promise<void> {
    if (!nmconsecOrdenServDetalle) return;

    try {
      const response = await this.cumplimientoRepo.obtenerSaldoOrden(
        nmconsecOrdenServDetalle
      );
      this.saldoActual.set(response.saldoPorEjecutar);
    } catch {
      // Usar saldo de la programación si falla la consulta
    }
  }

  // =========================================================================
  // CUMPLIMIENTO TARDÍO - Legacy: CreateOrUpdateCumplimientoActivity + UtilitiesDate
  // =========================================================================

  private normalizarLimitDaysProgram(valor: unknown): number {
    const normalizado = Number.parseInt(String(valor ?? '').trim(), 10);
    if (Number.isNaN(normalizado) || normalizado < 0) {
      return 0;
    }
    return normalizado;
  }

  /**
   * Carga la configuración para validación de cumplimiento tardío.
   *
   * Legacy:
   * - CreateOrUpdateCumplimientoActivity.loadParameters() → LIMIT_DAYS_PROGRAM
   * - CreateOrUpdateCumplimientoActivity.loadParametersHoliDays() → en el legacy, parámetro
   *   HOLIDAYS; aquí, T094_DIAS_FESTIVOS vía agenda-ms
   *
   * Debe llamarse al abrir el formulario de cumplimiento.
   */
  async cargarConfiguracionTardio(): Promise<void> {
    if (this.loadingConfigTardio()) return;

    this.loadingConfigTardio.set(true);
    this.configTardioError.set(false);
    try {
      // 1. Cargar configuración de cumplimiento desde endpoint de dominio
      const configCumplimiento =
        await this.cumplimientoRepo.obtenerConfiguracionCumplimiento();
      this.limitDaysProgram.set(
        this.normalizarLimitDaysProgram(configCumplimiento?.limitDaysProgram)
      );
      this.contratosCumpl.set(configCumplimiento.contratosCumpl ?? []);

      // 2. Cargar festivos desde agenda-ms (T094_DIAS_FESTIVOS, misma fuente que valida el backend)
      const configAgenda = await firstValueFrom(
        this.http.get<{ festivos: string[] }>(
          `${environment.backend.baseUrl}/agenda/configuracion`
        )
      );
      if (configAgenda?.festivos) {
        this.festivos.set(configAgenda.festivos);
      }

      // 3. Cargar justificaciones para cumplimiento tardio
      // Si falla esta llamada, no debe invalidar la configuracion principal.
      try {
        const justificacionesApi = await firstValueFrom(
          this.http.get<Array<{ codigo: string | null; descripcion: string }>>(
            `${environment.backend.baseUrl}/servicios/catalogos/justificaciones-cancelacion`
          )
        );

        const justificaciones = (justificacionesApi ?? [])
          .filter((j) => j?.codigo !== null && j?.codigo !== undefined)
          .map((j) => ({
            label: j.descripcion,
            value: Number(j.codigo)
          }))
          .filter((j) => !Number.isNaN(j.value));

        this.justificaciones.set(justificaciones);
      } catch {
        this.justificaciones.set([]);
      }

      if (!environment.production) {
        console.log('[CUMPLIMIENTO TARDÍO] Configuración cargada:', {
          limitDaysProgram: this.limitDaysProgram(),
          festivos: this.festivos().length
        });
      }
    } catch (error) {
      if (!environment.production)
        console.error(
          '[CUMPLIMIENTO TARDÍO] Error cargando configuración:',
          error
        );
      // No se asumen valores por defecto (límite 0 y sin festivos): se conserva lo ya cargado
      // y se avisa, porque sin la configuración real no se puede decidir la tardanza.
      this.configTardioError.set(true);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail:
          'No se pudo cargar la configuración (límite de días y festivos) para validar si el cumplimiento es tardío. Intente nuevamente.',
        life: 5000
      });
    } finally {
      this.loadingConfigTardio.set(false);
    }
  }

  /**
   * Verificar si cumplimiento es tardío.
   *
   * Legacy: CreateOrUpdateCumplimientoViewImpl.cumplimientoTardio()
   *
   * La lógica del legacy:
   * 1. Calcula días hábiles desde fecha_programacion hasta hoy (excluyendo fines de semana y festivos)
   * 2. Si días hábiles > LIMIT_DAYS_PROGRAM → Es tardío → Requiere justificación
   *
   * @param fechaCumplimiento Fecha del cumplimiento (normalmente hoy)
   */
  verificarCumplimientoTardio(fechaCumplimiento: Date): void {
    const programacion = this.selectedProgramacion();
    if (!programacion?.feprogramacion) return;

    const fechaProg = new Date(programacion.feprogramacion);

    // Legacy: UtilitiesDate.daysDiffHoliDays(dfFechaCumplimiento.getDate(), getHoliDays())
    // Usa utilidad compartida de @common/date-utils
    const diasHabiles = calcularDiasHabiles(
      fechaProg,
      fechaCumplimiento,
      this.festivos()
    );

    // Legacy: daysDiffHoliDays(...) > Integer.parseInt(getValueParameter())
    const esTardio = diasHabiles > this.limitDaysProgram();

    if (!environment.production) {
      console.log('[CUMPLIMIENTO TARDÍO] Verificación:', {
        fechaProgramacion: dateToString(fechaProg),
        fechaCumplimiento: dateToString(fechaCumplimiento),
        diasHabiles,
        limitDaysProgram: this.limitDaysProgram(),
        esTardio
      });
    }

    this.requiereJustificacion.set(esTardio);
  }

  /**
   * Buscar municipios para autocomplete
   */
  buscarMunicipios(query: string): void {
    if (!query || query.length < 2) return;

    // TODO: Llamar API real cuando esté disponible
    const municipios: Municipio[] = [
      {
        cdsura: '05001',
        cdmunicipio: '05001',
        nombre: 'Medellín',
        departamento: 'Antioquia'
      },
      {
        cdsura: '11001',
        cdmunicipio: '11001',
        nombre: 'Bogotá',
        departamento: 'Cundinamarca'
      },
      {
        cdsura: '76001',
        cdmunicipio: '76001',
        nombre: 'Cali',
        departamento: 'Valle del Cauca'
      },
      {
        cdsura: '08001',
        cdmunicipio: '08001',
        nombre: 'Barranquilla',
        departamento: 'Atlántico'
      },
      {
        cdsura: '13001',
        cdmunicipio: '13001',
        nombre: 'Cartagena',
        departamento: 'Bolívar'
      }
    ].filter((m) => m.nombre.toLowerCase().includes(query.toLowerCase()));

    this.municipiosSugeridos.set(municipios);
  }

  /**
   * Seleccionar municipio origen
   */
  seleccionarMunicipioOrigen(municipio: Municipio): void {
    this.municipioOrigen.set(municipio);
    this.calcularTarifa();
  }

  /**
   * Seleccionar municipio destino
   */
  seleccionarMunicipioDestino(municipio: Municipio): void {
    this.municipioDestino.set(municipio);
    this.calcularTarifa();
  }

  /**
   * Calcular tarifa de transporte
   */
  private calcularTarifa(): void {
    const origen = this.municipioOrigen();
    const destino = this.municipioDestino();

    if (!origen || !destino) return;

    this.calculandoTarifa.set(true);

    // Usamos CDSURA según lección aprendida. La URL (con
    // environment.backend.baseUrl) la arma el adapter del puerto de transporte.
    this.poblacionRepo
      .obtenerTarifa(origen.cdsura, destino.cdsura)
      .subscribe({
        next: () => {
          this.calculandoTarifa.set(false);
          // El valor se usa directamente desde el computed en el componente
        },
        error: () => {
          this.calculandoTarifa.set(false);
        }
      });
  }

  /**
   * Guardar cumplimiento
   */
  async guardarCumplimiento(request: CumplimientoRequest): Promise<void> {
    if (this.formLoading()) {
      return;
    }

    if (!environment.production)
      console.log('Facade: guardarCumplimiento iniciado', request);
    this.formLoading.set(true);

    try {
      const result = (
        this.origenInformacionTareas()
          ? await this.ordenServicioRepo.modificarCumplimientoInformacionTareas(
              request as unknown as Record<string, unknown>
            )
          : await this.cumplimientoRepo.registrarCumplimiento(request)
      ) as any;
      if (!this.origenInformacionTareas()) {
        this.contactosInformePendiente.set(gestionContactosInformeSiAplica(request, result));
      }
      if (!environment.production)
        console.log('Facade: cumplimiento guardado exitosamente', result);

      await this.actualizarContactoCumplimientoSiAplica(
        result.nmconsecutivo,
        request
      );

      if (result.warningOutlook) {
        const detalleOutlook =
          result.mensajeWarningOutlook?.trim() ||
          'El cumplimiento fue registrado, pero no se pudo sincronizar la cita con Outlook.';
        this.messageService.add({
          severity: 'warn',
          summary: 'Sincronizacion Outlook pendiente',
          detail: detalleOutlook,
          life: 8000
        });
      }

      if (result.warningPostPersistencia) {
        const detallePostPersistencia =
          result.mensajeWarningPostPersistencia?.trim() ||
          'El cumplimiento fue registrado, pero no se pudo guardar toda la informacion adicional.';
        this.messageService.add({
          severity: 'warn',
          summary: 'Informacion complementaria pendiente',
          detail: detallePostPersistencia,
          life: 8000
        });
      }

      // Si aplica acta y su generación falló, mantener formulario abierto para reintento.
      if (result.asociarActa && result.idActa360 == null) {
        this.nmconsecutivoCumplimientoGuardado = result.nmconsecutivo;
        this.ultimaDatosActa = request.datosActa ?? null;
        this.mostrarBotonReintentar.set(true);
        const detalleBackend = result.actaMensajeError ?? undefined;
        const mensajeAlerta = detalleBackend?.trim()
          ? detalleBackend
          : 'El cumplimiento fue guardado, pero no se pudo generar el acta 360.';
        this.actaError.set(mensajeAlerta);
        this.messageService.add({
          severity: 'success',
          summary: 'Cumplimiento registrado',
          detail:
            'La orientación fue registrada. Puede reintentar la generación del acta.',
          life: 5000
        });
        this.messageService.add({
          severity: 'warn',
          summary: 'Acta pendiente',
          detail: mensajeAlerta,
          life: 8000
        });
        return;
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Cumplimiento registrado',
        detail: 'La orientación programada fue registrada correctamente.',
        life: 4000
      });
      if (result.idActa360) {
        this.nmconsecutivoCumplimientoDescargable = result.nmconsecutivo;
        this.mostrarBotonDescargarActa.set(true);
        this.messageService.add({
          severity: 'success',
          summary: 'Acta asociada',
          detail: `El acta ${result.idActa360} fue generada y asociada al cumplimiento.`,
          life: 4000
        });
        await this.buscar();
        return;
      }

      this.cerrarFormulario();
      // Refrescar listado
      await this.buscar();
    } catch (err: any) {
      if (!environment.production)
        console.error('Facade: error al guardar cumplimiento', err);
      this.formLoading.set(false);
      const errorMsg =
        err.error?.detail ||
        err.error?.message ||
        err.message ||
        'Error al registrar el cumplimiento';
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: errorMsg,
        life: 5000
      });
    } finally {
      this.formLoading.set(false);
    }
  }

  async onReintentarActa(datosActa?: DatosActaRequest): Promise<void> {
    if (this.actaGenerando()) {
      return;
    }

    const datosActaReintento = datosActa ?? this.ultimaDatosActa;
    if (!this.nmconsecutivoCumplimientoGuardado || !datosActaReintento) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Aviso',
        detail: 'No hay datos del acta para reintentar.'
      });
      return;
    }

    this.ultimaDatosActa = datosActaReintento;

    this.actaGenerando.set(true);
    this.actaError.set(null);
    const nmconsecutivo = this.nmconsecutivoCumplimientoGuardado;

    try {
      const payload = {
        ...datosActaReintento,
        idCumplimiento: String(nmconsecutivo)
      };
      const result = await this.cumplimientoRepo.reintentarGenerarActa(
        nmconsecutivo,
        payload
      );

      this.mostrarBotonReintentar.set(false);
      this.nmconsecutivoCumplimientoDescargable = nmconsecutivo;
      this.mostrarBotonDescargarActa.set(true);
      this.messageService.add({
        severity: 'success',
        summary: 'Acta generada',
        detail: `Acta generada exitosamente (ID: ${result.idActa360})`,
        life: 5000
      });
      await this.buscar();
    } catch (err: any) {
      const msg =
        err?.error?.detail ||
        err?.error?.message ||
        err?.message ||
        'Error al reintentar la generación del acta';
      this.actaError.set(msg);
      this.messageService.add({
        severity: 'error',
        summary: 'Error al generar acta',
        detail: msg,
        life: 8000
      });
    } finally {
      this.actaGenerando.set(false);
    }
  }

  async descargarActaAsociada(): Promise<void> {
    const nmconsecutivo = this.nmconsecutivoCumplimientoDescargable;
    if (!nmconsecutivo) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Sin acta asociada',
        detail: 'No hay un acta asociada disponible para descargar.'
      });
      return;
    }

    this.descargandoActa.set(true);
    try {
      await this.cumplimientoRepo.descargarActaPdf(
        nmconsecutivo,
        `acta_${nmconsecutivo}.pdf`
      );
      this.messageService.add({
        severity: 'success',
        summary: 'Descarga iniciada',
        detail: 'El acta asociada se está descargando.'
      });
    } catch (error) {
      const mensaje =
        error instanceof Error
          ? error.message
          : 'No fue posible descargar el acta asociada.';
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: mensaje
      });
    } finally {
      this.descargandoActa.set(false);
    }
  }

  // =========================================================================
  // UTILIDADES
  // =========================================================================

  formatFecha(fecha: string | undefined): string {
    if (!fecha) return '';
    const date = new Date(fecha);
    return date.toLocaleDateString('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Mapea tipo de cita a severity de PrimeNG Tag
   * Legacy: TPSC_TIPOS_DE_CITAS - códigos 17, 18, 42, 43
   */
  getTipoSeverity(
    nmtipoCita: number | string | undefined
  ): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    if (!nmtipoCita) return 'secondary';
    const tipo =
      typeof nmtipoCita === 'string' ? parseInt(nmtipoCita, 10) : nmtipoCita;
    switch (tipo) {
      case TIPO_CITA.CAMPO:
        return 'info'; // 17
      case TIPO_CITA.INFORME:
        return 'success'; // 18
      case TIPO_CITA.CAMPO_COMPLEMENTARIO:
        return 'warn'; // 42
      case TIPO_CITA.INFORME_COMPLEMENTARIO:
        return 'warn'; // 43
      default:
        return 'secondary';
    }
  }

  /**
   * Mapea código de estado a severity de PrimeNG Tag
   * Legacy: TPSC_ESTADOS - códigos '0015', '0016', '0017', '0019'
   */
  getEstadoSeverity(
    cdestado: string | undefined
  ): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' {
    switch (cdestado) {
      case ESTADO_CITA.PROGRAMADA:
        return 'info'; // 0015 - Programado
      case ESTADO_CITA.EJECUTADA:
        return 'success'; // 0016 - Ejecutado
      case ESTADO_CITA.CANCELADA:
        return 'danger'; // 0017 - Cancelado
      case ESTADO_CITA.RESERVADA:
        return 'warn'; // 0019 - Reservado
      default:
        return 'secondary';
    }
  }
}
